import { useState } from 'react';
import type { DocItem, DocFile } from '@/mocks/documents';

interface DocEditorProps {
  title: string;
  items: DocItem[];
  storageKey: string;
  onUpdate: (items: DocItem[]) => void;
}

interface FileRowProps {
  file: DocFile;
  idx: number;
  isUploading: boolean;
  onUpload: (f: File) => void;
  onUpdate: (field: keyof DocFile, val: string) => void;
  onRemove: () => void;
}

function FileRowItem({ file, idx, isUploading, onUpload, onUpdate, onRemove }: FileRowProps) {
  const hasFile = Boolean(file.url && file.url.trim());
  const inputId = `file-upload-${idx}-${Math.random().toString(36).substring(2, 9)}`;

  return (
    <div className="p-3 bg-background-950/80 border border-background-800 rounded-lg space-y-2">
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        {/* Name Input */}
        <div className="flex-1">
          <input
            value={file.label}
            onChange={(e) => onUpdate('label', e.target.value)}
            placeholder="Название файла (например: Устав на казахском)"
            className="w-full bg-background-900 border border-background-700/80 rounded px-3 py-2 text-xs text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-accent-500"
          />
        </div>

        {/* Upload Button */}
        <div className="flex items-center gap-2">
          <input
            type="file"
            id={inputId}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.txt"
            className="hidden"
            onChange={(e) => {
              const selected = e.target.files?.[0];
              if (selected) onUpload(selected);
              e.target.value = ''; // reset so same file can be chosen again
            }}
          />

          <label
            htmlFor={inputId}
            className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded text-xs font-label font-bold transition-all cursor-pointer whitespace-nowrap select-none ${
              hasFile
                ? 'bg-accent-500/20 text-accent-400 border border-accent-500/40 hover:bg-accent-500/30'
                : 'bg-accent-500 hover:bg-accent-600 text-background-950 shadow-md'
            }`}
          >
            <i className={isUploading ? 'ri-loader-4-line animate-spin' : hasFile ? 'ri-refresh-line' : 'ri-upload-2-line'} />
            <span>{isUploading ? 'Загрузка...' : hasFile ? 'Заменить файл' : 'Загрузить файл'}</span>
          </label>

          <button
            type="button"
            onClick={onRemove}
            className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-red-400 hover:bg-background-800/60 rounded transition-colors cursor-pointer"
            title="Удалить файл"
          >
            <i className="ri-delete-bin-line text-sm" />
          </button>
        </div>
      </div>

      {/* File status */}
      {hasFile && (
        <div className="flex items-center gap-2 text-[11px] text-green-400 font-label bg-green-500/10 px-2.5 py-1 rounded border border-green-500/20 truncate">
          <i className="ri-checkbox-circle-fill text-green-400 flex-shrink-0" />
          <span className="truncate">
            Прикреплен: {file.url.startsWith('data:') ? 'Локальный файл (Base64)' : file.url}
          </span>
        </div>
      )}
    </div>
  );
}

export default function DocEditor({ title, items, storageKey, onUpdate }: DocEditorProps) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<DocItem | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  const [newItem, setNewItem] = useState<DocItem>({ id: '', title: '', files: [{ label: '', url: '' }] });
  const [saved, setSaved] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState<number | null>(null);

  const docType = storageKey.includes('patriot') ? 'patriot' : 'general';

  const save = async (updated: DocItem[]) => {
    onUpdate(updated);
    localStorage.setItem(storageKey, JSON.stringify(updated));

    // Persist to server API if available
    try {
      await fetch('/api/docs.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: docType, items: updated }),
      });
    } catch {
      // offline / fallback
    }

    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const deleteItem = (id: string) => {
    if (!window.confirm('Удалить документ?')) return;
    save(items.filter((i) => i.id !== id));
  };

  const startEdit = (item: DocItem) => {
    setEditingItem(JSON.parse(JSON.stringify(item)));
    setExpanded(item.id);
    setAddingNew(false);
  };

  const saveEdit = () => {
    if (!editingItem || !editingItem.title.trim()) return;
    save(items.map((i) => (i.id === editingItem.id ? editingItem : i)));
    setEditingItem(null);
  };

  const saveNew = () => {
    if (!newItem.title.trim()) return;
    const item: DocItem = {
      ...newItem,
      id: `doc-${Date.now()}`,
      files: newItem.files.filter((f) => f.label.trim() || f.url.trim()),
    };
    save([...items, item]);
    setNewItem({ id: '', title: '', files: [{ label: '', url: '' }] });
    setAddingNew(false);
  };

  const handleFileUpload = async (file: File, isEdit: boolean, idx: number) => {
    setUploadingIdx(idx);
    const cleanFileName = file.name.replace(/\.[^/.]+$/, '');

    try {
      // 1. Try server upload
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch('/api/upload.php', {
        method: 'POST',
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success && data.url) {
          if (isEdit && editingItem) {
            const files = [...editingItem.files];
            files[idx] = {
              label: files[idx]?.label?.trim() || cleanFileName,
              url: data.url,
            };
            setEditingItem({ ...editingItem, files });
          } else {
            const files = [...newItem.files];
            files[idx] = {
              label: files[idx]?.label?.trim() || cleanFileName,
              url: data.url,
            };
            setNewItem({ ...newItem, files });
          }
          setUploadingIdx(null);
          return;
        }
      }
    } catch {
      // Fallback
    }

    // 2. Fallback to FileReader Base64
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      if (isEdit && editingItem) {
        const files = [...editingItem.files];
        files[idx] = {
          label: files[idx]?.label?.trim() || cleanFileName,
          url: dataUrl,
        };
        setEditingItem({ ...editingItem, files });
      } else {
        const files = [...newItem.files];
        files[idx] = {
          label: files[idx]?.label?.trim() || cleanFileName,
          url: dataUrl,
        };
        setNewItem({ ...newItem, files });
      }
      setUploadingIdx(null);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="bg-background-900/60 rounded-xl border border-background-700/40 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-background-800/40">
        <h3 className="font-heading text-foreground-200 text-sm tracking-widest uppercase">{title}</h3>
        <div className="flex items-center gap-2">
          {saved && (
            <span className="text-xs text-green-400 font-label tracking-wide flex items-center gap-1">
              <i className="ri-check-line" /> Сохранено
            </span>
          )}
          <button
            onClick={() => {
              setAddingNew(true);
              setEditingItem(null);
            }}
            className="flex items-center gap-2 px-3.5 py-1.5 bg-accent-500 hover:bg-accent-600 text-background-950 rounded text-xs font-label font-bold tracking-wide transition-colors cursor-pointer whitespace-nowrap shadow"
          >
            <i className="ri-add-line" /> Добавить документ
          </button>
        </div>
      </div>

      {/* List */}
      <div className="p-4 space-y-3 max-h-[600px] overflow-y-auto">
        {items.length === 0 && !addingNew && (
          <div className="text-center py-8 text-foreground-500 text-xs font-label">
            Документов пока нет. Нажмите «Добавить документ» выше.
          </div>
        )}

        {items.map((item) => {
          const isEditing = editingItem?.id === item.id;
          const isOpen = expanded === item.id;

          return (
            <div key={item.id} className="border border-background-700/50 rounded-lg overflow-hidden bg-background-950/40">
              <div className="flex items-start gap-3 px-4 py-3 bg-background-800/40">
                <button
                  onClick={() => setExpanded(isOpen ? null : item.id)}
                  className="mt-0.5 w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-500 hover:text-foreground-300 cursor-pointer"
                >
                  <i className={`ri-${isOpen ? 'subtract-line' : 'add-line'} text-sm`} />
                </button>
                <p className="flex-1 text-xs text-foreground-200 font-medium leading-relaxed">{item.title}</p>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => startEdit(item)}
                    className="w-7 h-7 flex items-center justify-center text-foreground-400 hover:text-accent-400 transition-colors cursor-pointer"
                    title="Редактировать"
                  >
                    <i className="ri-edit-line text-sm" />
                  </button>
                  <button
                    onClick={() => deleteItem(item.id)}
                    className="w-7 h-7 flex items-center justify-center text-foreground-400 hover:text-red-400 transition-colors cursor-pointer"
                    title="Удалить"
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
              </div>

              {isOpen && !isEditing && (
                <div className="px-4 py-3 border-t border-background-700/30 bg-background-900/60">
                  <p className="text-[11px] text-foreground-500 uppercase tracking-wider mb-2 font-label">Прикрепленные файлы:</p>
                  <ul className="space-y-1.5">
                    {item.files.map((f, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-xs">
                        <i className="ri-file-pdf-line text-accent-500 text-sm flex-shrink-0" />
                        <a
                          href={f.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent-400 hover:text-accent-300 underline underline-offset-2 truncate"
                        >
                          {f.label || 'Документ'}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Edit Mode */}
              {isEditing && editingItem && (
                <div className="px-4 py-4 border-t border-background-700/40 bg-background-950/90 space-y-4">
                  <div>
                    <label className="block text-xs text-foreground-400 font-label tracking-wide mb-1">
                      Заголовок документа *
                    </label>
                    <textarea
                      value={editingItem.title}
                      onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                      rows={2}
                      className="w-full bg-background-900 border border-background-700 rounded px-3 py-2 text-xs text-foreground-100 focus:outline-none focus:border-accent-500 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-foreground-400 font-label tracking-wide mb-2">
                      Файлы документа
                    </label>
                    <div className="space-y-2">
                      {editingItem.files.map((f, idx) => (
                        <FileRowItem
                          key={idx}
                          file={f}
                          idx={idx}
                          isUploading={uploadingIdx === idx}
                          onUpload={(file) => handleFileUpload(file, true, idx)}
                          onUpdate={(field, val) => {
                            const files = [...editingItem.files];
                            files[idx] = { ...files[idx], [field]: val };
                            setEditingItem({ ...editingItem, files });
                          }}
                          onRemove={() => {
                            const files = editingItem.files.filter((_, i) => i !== idx);
                            setEditingItem({ ...editingItem, files: files.length ? files : [{ label: '', url: '' }] });
                          }}
                        />
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={() => setEditingItem({ ...editingItem, files: [...editingItem.files, { label: '', url: '' }] })}
                      className="mt-2 text-xs text-accent-400 hover:text-accent-300 font-label flex items-center gap-1 cursor-pointer"
                    >
                      <i className="ri-add-circle-line" /> Добавить еще один файл
                    </button>
                  </div>

                  <div className="flex gap-2 pt-2 border-t border-background-800">
                    <button
                      onClick={saveEdit}
                      className="px-5 py-2 bg-accent-500 hover:bg-accent-600 text-background-950 font-bold text-xs font-label rounded cursor-pointer whitespace-nowrap shadow"
                    >
                      Сохранить изменения
                    </button>
                    <button
                      onClick={() => setEditingItem(null)}
                      className="px-4 py-2 border border-background-600 text-foreground-400 hover:text-foreground-200 text-xs font-label rounded cursor-pointer whitespace-nowrap"
                    >
                      Отмена
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Add New Document Form */}
        {addingNew && (
          <div className="border-2 border-accent-500/40 rounded-xl p-5 bg-background-950/95 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <p className="text-xs font-heading tracking-widest text-accent-400 uppercase">Новый документ</p>
              <button onClick={() => setAddingNew(false)} className="text-foreground-500 hover:text-foreground-300">
                <i className="ri-close-line text-base" />
              </button>
            </div>

            <div>
              <label className="block text-xs text-foreground-400 font-label tracking-wide mb-1">
                Заголовок документа *
              </label>
              <textarea
                value={newItem.title}
                onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                rows={2}
                className="w-full bg-background-900 border border-background-700 rounded px-3 py-2 text-xs text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-accent-500 resize-none"
                placeholder="Введите название/заголовок документа..."
              />
            </div>

            <div>
              <label className="block text-xs text-foreground-400 font-label tracking-wide mb-2">
                Прикрепить файлы
              </label>
              <div className="space-y-2">
                {newItem.files.map((f, idx) => (
                  <FileRowItem
                    key={idx}
                    file={f}
                    idx={idx}
                    isUploading={uploadingIdx === idx}
                    onUpload={(file) => handleFileUpload(file, false, idx)}
                    onUpdate={(field, val) => {
                      const files = [...newItem.files];
                      files[idx] = { ...files[idx], [field]: val };
                      setNewItem({ ...newItem, files });
                    }}
                    onRemove={() => {
                      const files = newItem.files.filter((_, i) => i !== idx);
                      setNewItem({ ...newItem, files: files.length ? files : [{ label: '', url: '' }] });
                    }}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={() => setNewItem({ ...newItem, files: [...newItem.files, { label: '', url: '' }] })}
                className="mt-2 text-xs text-accent-400 hover:text-accent-300 font-label flex items-center gap-1 cursor-pointer"
              >
                <i className="ri-add-circle-line" /> Добавить еще один файл
              </button>
            </div>

            <div className="flex gap-2 pt-2 border-t border-background-800">
              <button
                onClick={saveNew}
                disabled={!newItem.title.trim()}
                className="px-5 py-2 bg-accent-500 hover:bg-accent-600 text-background-950 font-bold text-xs font-label rounded cursor-pointer whitespace-nowrap disabled:opacity-50 shadow"
              >
                Сохранить и опубликовать
              </button>
              <button
                onClick={() => setAddingNew(false)}
                className="px-4 py-2 border border-background-600 text-foreground-400 hover:text-foreground-200 text-xs font-label rounded cursor-pointer whitespace-nowrap"
              >
                Отмена
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}